import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Globe, Menu, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import Datenschutz from './Datenschutz';
import Impressum from './Impressum';
import { PRODUCT_URLS } from '../config/productLinks';
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  stripLocaleFromPath,
  withLocalePath,
} from '../i18n/config';
import { getMessages } from '../i18n/messages';

const WEBSITE_BASE_URL = 'https://www.lazzaro.one';

const OG_LOCALE_BY_LANGUAGE = {
  en: 'en_US',
  de: 'de_DE',
  it: 'it_IT',
  es: 'es_ES',
};

const upsertMetaByName = (name, content) => {
  let element = document.head.querySelector(`meta[name="${name}"]`);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('name', name);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
};

const upsertMetaByProperty = (property, content) => {
  let element = document.head.querySelector(`meta[property="${property}"]`);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('property', property);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
};

const upsertLink = (selector, attributes) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement('link');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
};

const HomePage = ({ locale = DEFAULT_LOCALE }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showImpressum, setShowImpressum] = useState(false);
  const [showDatenschutz, setShowDatenschutz] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const t = getMessages(locale);

  const navItems = useMemo(
    () => [
      { key: 'home', label: t.header.home },
      { key: 'products', label: t.header.products },
      { key: 'about', label: t.header.about },
      { key: 'contact', label: t.header.contact },
    ],
    [t],
  );

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const relativePath = stripLocaleFromPath(location.pathname);
    const localizedPath = withLocalePath(relativePath, locale);
    const canonicalUrl = `${WEBSITE_BASE_URL}${localizedPath}`;

    document.documentElement.lang = locale;
    document.title = t.seo.title;

    upsertMetaByName('description', t.seo.description);
    upsertMetaByProperty('og:title', t.seo.title);
    upsertMetaByProperty('og:description', t.seo.description);
    upsertMetaByProperty('og:url', canonicalUrl);
    upsertMetaByProperty(
      'og:locale',
      OG_LOCALE_BY_LANGUAGE[locale] ?? OG_LOCALE_BY_LANGUAGE.en,
    );
    upsertMetaByProperty('twitter:title', t.seo.title);
    upsertMetaByProperty('twitter:description', t.seo.description);

    upsertLink('link[rel="canonical"]', {
      rel: 'canonical',
      href: canonicalUrl,
    });

    SUPPORTED_LOCALES.forEach((language) => {
      upsertLink(`link[rel="alternate"][hreflang="${language}"]`, {
        rel: 'alternate',
        hreflang: language,
        href: `${WEBSITE_BASE_URL}${withLocalePath(relativePath, language)}`,
      });
    });

    upsertLink('link[rel="alternate"][hreflang="x-default"]', {
      rel: 'alternate',
      hreflang: 'x-default',
      href: `${WEBSITE_BASE_URL}/${DEFAULT_LOCALE}`,
    });
  }, [locale, location.pathname, t]);

  const switchLanguage = (nextLocale) => {
    const nextPath = withLocalePath(location.pathname, nextLocale);
    navigate(`${nextPath}${location.search}${location.hash}`);
    setMobileMenuOpen(false);
  };

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setMobileMenuOpen(false);
  };

  const currentYear = new Date().getFullYear();

  return (
    <>
      <div className="min-h-screen bg-white text-lazzaro-steel-900">
        <div
          aria-hidden="true"
          className="pointer-events-none fixed inset-0 -z-10 grid-pattern-subtle"
        />

        <header
          className={`fixed inset-x-0 top-0 z-40 border-b transition-colors ${
            scrolled
              ? 'border-lazzaro-steel-200 bg-white/98'
              : 'border-transparent bg-white/80'
          }`}
        >
          <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 sm:px-8 lg:px-12">
            <button
              type="button"
              onClick={() => scrollToSection('home')}
              className="flex items-center gap-3 text-left"
            >
              <div className="h-10 w-10 overflow-hidden rounded border border-lazzaro-steel-200 bg-white p-1.5">
                <img src="/logo.png" alt="Lazzaro One logo" className="h-full w-full object-contain" />
              </div>
              <span className="text-lg font-medium text-lazzaro-steel-900">
                Lazzaro One
              </span>
            </button>

            <div className="hidden items-center gap-10 md:flex">
              <nav className="flex items-center gap-8 text-sm">
                {navItems.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => scrollToSection(item.key)}
                    className="text-lazzaro-steel-600 transition-colors hover:text-lazzaro-steel-900"
                  >
                    {item.label}
                  </button>
                ))}
              </nav>

              <label className="flex items-center gap-2 text-sm text-lazzaro-steel-600">
                <Globe className="h-4 w-4" />
                <select
                  value={locale}
                  onChange={(event) => switchLanguage(event.target.value)}
                  className="rounded border border-lazzaro-steel-300 bg-white px-3 py-1.5 text-sm text-lazzaro-steel-900"
                >
                  {SUPPORTED_LOCALES.map((language) => (
                    <option key={language} value={language}>
                      {t.languages[language]}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <button
              type="button"
              className="rounded border border-lazzaro-steel-300 bg-white p-2 text-lazzaro-steel-900 md:hidden"
              aria-label={mobileMenuOpen ? t.header.closeMenu : t.header.openMenu}
              onClick={() => setMobileMenuOpen((previousValue) => !previousValue)}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="border-t border-lazzaro-steel-200 bg-white px-4 py-4 md:hidden">
              <nav className="flex flex-col gap-2">
                {navItems.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => scrollToSection(item.key)}
                    className="rounded px-3 py-2 text-left text-sm text-lazzaro-steel-900 transition-colors hover:bg-lazzaro-steel-50"
                  >
                    {item.label}
                  </button>
                ))}
              </nav>

              <label className="mt-4 flex items-center justify-between gap-3 rounded border border-lazzaro-steel-200 bg-lazzaro-steel-50 px-3 py-2 text-sm text-lazzaro-steel-600">
                <span>{t.header.language}</span>
                <select
                  value={locale}
                  onChange={(event) => switchLanguage(event.target.value)}
                  className="rounded border border-lazzaro-steel-300 bg-white px-2 py-1 text-sm text-lazzaro-steel-900"
                >
                  {SUPPORTED_LOCALES.map((language) => (
                    <option key={language} value={language}>
                      {t.languages[language]}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </header>

        <main className="mx-auto max-w-7xl px-6 pb-24 pt-32 sm:px-8 lg:px-12">
          <section id="home" className="grid gap-16 lg:grid-cols-12 lg:gap-20">
            <div className="lg:col-span-7">
              <div className="mb-8 inline-block border-l-2 border-lazzaro-blue-500 pl-4">
                <p className="text-sm font-semibold text-lazzaro-blue-600">{t.announcement}</p>
              </div>
              <h1 className="mb-8 max-w-2xl font-display text-4xl font-semibold leading-tight text-lazzaro-steel-900 sm:text-5xl lg:text-6xl">
                {t.hero.title}
              </h1>
              <p className="mb-10 max-w-xl text-lg leading-relaxed text-lazzaro-steel-600">
                {t.hero.subtitle}
              </p>
              <div className="flex flex-col gap-4 sm:flex-row">
                <button
                  type="button"
                  onClick={() => scrollToSection('products')}
                  className="inline-flex items-center justify-center gap-2 border border-lazzaro-steel-900 bg-lazzaro-steel-900 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-lazzaro-steel-800"
                >
                  {t.hero.primaryCta}
                </button>
                <button
                  type="button"
                  onClick={() => scrollToSection('contact')}
                  className="inline-flex items-center justify-center border border-lazzaro-steel-300 bg-white px-6 py-3 text-sm font-medium text-lazzaro-steel-900 transition-colors hover:border-lazzaro-steel-900"
                >
                  {t.hero.secondaryCta}
                </button>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="grid-pattern relative flex h-full min-h-[400px] flex-col rounded border border-lazzaro-steel-300 bg-white p-6 shadow-depth sm:p-8 lg:p-10">
                <div className="mb-8 sm:absolute sm:left-8 sm:top-8 sm:mb-0">
                  <div className="mb-2 flex items-center gap-2">
                    <div className="h-2 w-2 animate-pulse rounded-full bg-lazzaro-blue-500"></div>
                    <span className="text-xs font-medium uppercase tracking-wider text-lazzaro-steel-500">Live System</span>
                  </div>
                  <h3 className="font-display text-xl font-bold text-lazzaro-steel-900 sm:text-2xl">
                    Glass Box<br />Intelligence
                  </h3>
                </div>

                <div className="flex flex-1 flex-col justify-center sm:pt-8">
                  <div className="space-y-6 sm:space-y-8">
                    <div className="group flex items-start gap-3 transition-all hover:translate-x-2 sm:items-center sm:gap-4">
                      <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded border border-lazzaro-blue-200 bg-lazzaro-blue-50 sm:h-8 sm:w-8">
                          <span className="font-display text-xs font-bold text-lazzaro-blue-600">01</span>
                        </div>
                        <div className="h-1 w-12 bg-lazzaro-blue-500 transition-all group-hover:w-16 sm:w-20 sm:group-hover:w-24"></div>
                      </div>
                      <span className="text-sm font-medium text-lazzaro-steel-800">Mathematical precision</span>
                    </div>
                    <div className="group flex items-start gap-3 transition-all hover:translate-x-2 sm:items-center sm:gap-4">
                      <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded border border-lazzaro-sand-200 bg-lazzaro-sand-50 sm:h-8 sm:w-8">
                          <span className="font-display text-xs font-bold text-lazzaro-sand-700">02</span>
                        </div>
                        <div className="h-1 w-16 bg-lazzaro-sand-500 transition-all group-hover:w-20 sm:w-28 sm:group-hover:w-32"></div>
                      </div>
                      <span className="text-sm font-medium text-lazzaro-steel-800">Transparent algorithms</span>
                    </div>
                    <div className="group flex items-start gap-3 transition-all hover:translate-x-2 sm:items-center sm:gap-4">
                      <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded border border-lazzaro-blue-200 bg-lazzaro-blue-50 sm:h-8 sm:w-8">
                          <span className="font-display text-xs font-bold text-lazzaro-blue-600">03</span>
                        </div>
                        <div className="h-1 w-14 bg-lazzaro-blue-500 transition-all group-hover:w-18 sm:w-24 sm:group-hover:w-28"></div>
                      </div>
                      <span className="text-sm font-medium text-lazzaro-steel-800">Verifiable outputs</span>
                    </div>
                  </div>

                </div>

                <div className="mt-8 border-t border-lazzaro-steel-200 pt-6 sm:mt-12">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2">
                    <span className="font-display text-3xl font-bold text-lazzaro-steel-900">100%</span>
                    <span className="text-sm text-lazzaro-steel-600">Deterministic • Traceable • Auditable</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="company" className="mt-32">
            <div className="relative overflow-hidden border border-lazzaro-steel-200 bg-white shadow-subtle">
              <div className="grid-pattern-subtle absolute inset-0 opacity-30"></div>
              <div className="relative border-l-4 border-lazzaro-blue-500 pl-8 pr-8 py-12">
                <h2 className="mb-6 font-display text-3xl font-semibold text-lazzaro-steel-900 sm:text-4xl">{t.company.title}</h2>
                <p className="max-w-3xl text-xl leading-relaxed text-lazzaro-steel-600">{t.company.text}</p>
              </div>
            </div>
          </section>

          <section id="products" className="mt-32">
            <div className="mb-12 flex items-start gap-6">
              <div className="grid-pattern hidden h-16 w-16 shrink-0 rounded border border-lazzaro-steel-300 bg-white shadow-subtle sm:block"></div>
              <div>
                <h2 className="mb-4 font-display text-3xl font-semibold text-lazzaro-steel-900 sm:text-4xl">{t.products.title}</h2>
                <p className="max-w-2xl text-lg text-lazzaro-steel-600">{t.products.intro}</p>
              </div>
            </div>

            <div className="grid gap-8 md:grid-cols-2">
              <article className="group relative overflow-hidden border border-lazzaro-steel-200 bg-white p-8 shadow-subtle transition-all hover:border-lazzaro-blue-500 hover:shadow-depth-lg sm:p-10">
                <div className="absolute right-0 top-0 h-32 w-32 translate-x-8 -translate-y-8 rounded-full bg-lazzaro-blue-50 opacity-50 transition-all group-hover:scale-150"></div>
                <div className="relative">
                  <div className="mb-6 flex items-center gap-3">
                    <div className="h-1 w-12 bg-lazzaro-blue-500 transition-all group-hover:w-16"></div>
                    <p className="text-sm font-medium text-lazzaro-blue-600">{t.products.huax.label}</p>
                  </div>
                  <h3 className="mb-4 font-display text-2xl font-semibold text-lazzaro-steel-900">{t.products.huax.name}</h3>
                  <p className="mb-8 leading-relaxed text-lazzaro-steel-600">
                    {t.products.huax.description}
                  </p>
                  <a
                    href={PRODUCT_URLS.huax}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border-b-2 border-lazzaro-blue-500 pb-1 text-sm font-medium text-lazzaro-steel-900 transition-all hover:border-lazzaro-blue-700 hover:gap-3"
                  >
                    {t.products.huax.cta}
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              </article>

              <article className="group relative overflow-hidden border border-lazzaro-steel-200 bg-white p-8 shadow-subtle transition-all hover:border-lazzaro-sand-500 hover:shadow-depth-lg sm:p-10">
                <div className="absolute right-0 top-0 h-32 w-32 translate-x-8 -translate-y-8 rounded-full bg-lazzaro-sand-50 opacity-50 transition-all group-hover:scale-150"></div>
                <div className="relative">
                  <div className="mb-6 flex items-center gap-3">
                    <div className="h-1 w-12 bg-lazzaro-sand-500 transition-all group-hover:w-16"></div>
                    <p className="text-sm font-medium text-lazzaro-sand-600">{t.products.centeo.label}</p>
                  </div>
                  <h3 className="mb-4 font-display text-2xl font-semibold text-lazzaro-steel-900">{t.products.centeo.name}</h3>
                  <p className="mb-8 leading-relaxed text-lazzaro-steel-600">
                    {t.products.centeo.description}
                  </p>
                  <a
                    href={PRODUCT_URLS.centeo}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border-b-2 border-lazzaro-sand-500 pb-1 text-sm font-medium text-lazzaro-steel-900 transition-all hover:border-lazzaro-sand-700 hover:gap-3"
                  >
                    {t.products.centeo.cta}
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              </article>
            </div>
          </section>

          <section id="about" className="mt-32">
            <div className="grid gap-12 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <h2 className="mb-8 font-display text-3xl font-semibold text-lazzaro-steel-900 sm:text-4xl lg:text-5xl">
                  {t.about.title}
                </h2>
                <p className="mb-12 text-xl leading-relaxed text-lazzaro-steel-600">
                  {t.about.text}
                </p>

                <div className="grid gap-8 sm:grid-cols-2">
                  <div className="border-l-2 border-lazzaro-blue-500 pl-6">
                    <h3 className="mb-3 font-display text-lg font-semibold text-lazzaro-steel-900">
                      Why Glass Box?
                    </h3>
                    <p className="text-sm leading-relaxed text-lazzaro-steel-600">
                      In high-stakes environments, you cannot afford probabilistic black boxes. Our systems use deterministic models where every output can be traced back to its mathematical foundation.
                    </p>
                  </div>

                  <div className="border-l-2 border-lazzaro-sand-500 pl-6">
                    <h3 className="mb-3 font-display text-lg font-semibold text-lazzaro-steel-900">
                      Built for Complexity
                    </h3>
                    <p className="text-sm leading-relaxed text-lazzaro-steel-600">
                      Maritime sanctions, agricultural subsidy fraud, trade anomalies—these domains resist simple automation. We build specialized tools for specialized problems.
                    </p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="space-y-6">
                  <div className="group border border-lazzaro-steel-200 bg-white p-6 shadow-subtle transition-all hover:shadow-depth">
                    <div className="mb-4 flex items-center gap-3">
                      <div className="h-1 w-8 bg-lazzaro-blue-500"></div>
                      <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-lazzaro-steel-700">
                        Transparency
                      </h3>
                    </div>
                    <p className="mb-4 text-sm leading-relaxed text-lazzaro-steel-600">
                      Mathematical models, not black-box AI. Every calculation is traceable and verifiable.
                    </p>
                    <div className="flex items-baseline gap-2">
                      <span className="font-display text-2xl font-semibold text-lazzaro-steel-900">100%</span>
                      <span className="text-xs text-lazzaro-steel-500">deterministic outputs</span>
                    </div>
                  </div>

                  <div className="group border border-lazzaro-steel-200 bg-white p-6 shadow-subtle transition-all hover:shadow-depth">
                    <div className="mb-4 flex items-center gap-3">
                      <div className="h-1 w-8 bg-lazzaro-sand-500"></div>
                      <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-lazzaro-steel-700">
                        Precision
                      </h3>
                    </div>
                    <p className="mb-4 text-sm leading-relaxed text-lazzaro-steel-600">
                      Built for analysts who need confidence in their data, not probabilistic guesses.
                    </p>
                    <div className="flex items-baseline gap-2">
                      <span className="font-display text-2xl font-semibold text-lazzaro-steel-900">Zero</span>
                      <span className="text-xs text-lazzaro-steel-500">hallucinations or approximations</span>
                    </div>
                  </div>

                  <div className="group border border-lazzaro-steel-200 bg-white p-6 shadow-subtle transition-all hover:shadow-depth">
                    <div className="mb-4 flex items-center gap-3">
                      <div className="h-1 w-8 bg-lazzaro-blue-500"></div>
                      <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-lazzaro-steel-700">
                        Operational
                      </h3>
                    </div>
                    <p className="mb-4 text-sm leading-relaxed text-lazzaro-steel-600">
                      Intelligence systems designed for real-world decisions under time pressure.
                    </p>
                    <div className="flex items-baseline gap-2">
                      <span className="font-display text-2xl font-semibold text-lazzaro-steel-900">Real-time</span>
                      <span className="text-xs text-lazzaro-steel-500">processing for urgent decisions</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="contact" className="mt-32">
            <div className="mb-10">
              <h2 className="mb-4 font-display text-3xl font-semibold text-lazzaro-steel-900 sm:text-4xl">{t.contact.title}</h2>
              <p className="max-w-2xl text-lg text-lazzaro-steel-600">{t.contact.text}</p>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <div className="group border border-lazzaro-steel-200 bg-white p-6 shadow-subtle transition-all hover:border-lazzaro-blue-500 hover:shadow-depth">
                <p className="mb-3 text-sm font-medium text-lazzaro-steel-500">{t.contact.emailLabel}</p>
                <a
                  href="mailto:office@lazzaro.one"
                  className="text-xl font-medium text-lazzaro-steel-900 transition-colors hover:text-lazzaro-blue-600"
                >
                  office@lazzaro.one
                </a>
              </div>
              <div className="border border-lazzaro-steel-200 bg-white p-6 shadow-subtle transition-all hover:shadow-depth">
                <p className="mb-3 text-sm font-medium text-lazzaro-steel-500">{t.contact.companyLabel}</p>
                <p className="text-xl font-medium text-lazzaro-steel-900">Lazzaro One UG (haftungsbeschränkt)</p>
              </div>
            </div>

            <p className="mt-8 text-sm text-lazzaro-steel-500">{t.contact.mediaLine}</p>
          </section>
        </main>

        <footer className="mt-32 border-t border-lazzaro-steel-200 bg-lazzaro-steel-50">
          <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-12 text-sm text-lazzaro-steel-600 sm:px-8 lg:px-12">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="font-medium text-lazzaro-steel-900">{t.footer.companyLegal}</p>
              <div className="flex items-center gap-6">
                <button
                  type="button"
                  onClick={() => setShowImpressum(true)}
                  className="text-lazzaro-steel-600 transition-colors hover:text-lazzaro-steel-900"
                >
                  {t.footer.legal}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDatenschutz(true)}
                  className="text-lazzaro-steel-600 transition-colors hover:text-lazzaro-steel-900"
                >
                  {t.footer.privacy}
                </button>
              </div>
            </div>

            <p className="text-lazzaro-steel-500">{t.footer.productLine}</p>
            <p className="text-lazzaro-steel-500">
              &copy; {currentYear} Lazzaro One. {t.footer.rights}
            </p>
          </div>
        </footer>
      </div>

      <Impressum isOpen={showImpressum} onClose={() => setShowImpressum(false)} />
      <Datenschutz isOpen={showDatenschutz} onClose={() => setShowDatenschutz(false)} />
    </>
  );
};

export default HomePage;
